import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PawPrint, MapPin, Heart } from "@phosphor-icons/react";

const IMG = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/";
const HERO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/2pfs8ejt_image-7%20%282%29.jpeg";

const STATIONS = [
  { id: "gates", n: 1, x: "18%", y: "72%", title: "The Gates", img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/2pfs8ejt_image-7%20%282%29.jpeg",
    body: "A forged archway at the foot of the mountain: ALL DOGS GO TO HEAVEN. Every rescue passes under it on the way to a life that finally belongs to them." },
  { id: "road", n: 2, x: "50%", y: "40%", title: "The Long Road", img: "a5c272303f9221894a6f556082185834f66aea6fb47a24fa0d5bddec6b12322a",
    body: "A winding mile of dirt climbing the valley. The last hard road any of them will ever walk — because it only leads home." },
  { id: "house", n: 3, x: "70%", y: "62%", title: "The Main House", img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/7zf6knya_image-8.jpeg",
    body: "Wraparound porch, warm windows, a fire always going. Command center for the pack and the crew who care for them around the clock." },
  { id: "pond", n: 4, x: "34%", y: "55%", title: "Cabins & the Pond", img: "8cfdb265e2d74617a48417cfa01068f885e4fff86c3af43e27d2ac1ad6f345e0",
    body: "A quiet pond ringed by cozy camping cabins and a giant treehouse — a place supporters can come, stay, and meet the dogs they helped save." },
  { id: "field", n: 5, x: "62%", y: "80%", title: "The Welcome Field", img: "487377ec66b01e1e81c57d55bd93ba692bdcc7111150001ce0a85d58c18d2f84",
    body: "Open green where every newcomer is met by the whole pack running to greet them. Nobody arrives at Heaven alone." },
  { id: "meadow", n: 6, x: "84%", y: "34%", title: "Memorial Meadow", img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/82qnwmxq_image-2.jpeg",
    body: "The quiet rise kept for Smiley, Beats & King — and every pit who left too soon. Their aura watches over the whole ranch." },
];

export default function Ranch() {
  return (
    <div data-testid="ranch-page">
      {/* hero + interactive map */}
      <section className="relative h-[70vh] min-h-[460px] overflow-hidden">
        <img src={HERO} alt="Heaven ranch driveway at the foot of the mountain" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/20" />
        {STATIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} className="group absolute -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: s.x, top: s.y }} data-testid={`ranch-pin-${s.id}`}>
            <span className="relative grid place-items-center h-9 w-9 rounded-full bg-primary text-primary-foreground font-modern text-sm neon-glow group-hover:scale-110 transition-transform">
              {s.n}
              <span className="absolute inset-0 rounded-full border-2 border-primary animate-ping opacity-40" />
            </span>
          </a>
        ))}
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-7xl mx-auto">
          <p className="overline text-primary mb-2 inline-flex items-center gap-2"><MapPin weight="fill" size={14} /> All Dogs Go to Heaven · 300 acres</p>
          <h1 className="font-display text-5xl sm:text-6xl leading-[0.95] max-w-3xl">Welcome to <span className="neon-text">Heaven.</span></h1>
          <p className="text-muted-foreground mt-4 max-w-xl">Tap a marker to walk the ranch we're building — the sanctuary that ends the road for 3,000 pits.</p>
        </div>
      </section>

      {/* stations */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 space-y-16">
        {STATIONS.map((s, idx) => (
          <motion.div key={s.id} id={s.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
            className={`grid md:grid-cols-2 gap-8 items-center ${idx % 2 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`ranch-station-${s.id}`}>
            <div className="rounded-2xl overflow-hidden border border-primary/20 neon-glow">
              <img src={s.img.startsWith("http") ? s.img : IMG + s.img + ".jpeg"} alt={s.title} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="overline text-secondary mb-2">Station {s.n}</p>
              <h2 className="font-display text-3xl sm:text-4xl mb-4">{s.title}</h2>
              <p className="text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24 text-center">
        <h2 className="font-display text-3xl sm:text-4xl mb-4">Help us open the gates.</h2>
        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">Heaven isn't a dream — it's a build. Every hoodie and every dollar lays another fence post.</p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/donate" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="ranch-donate-cta">
            <Heart weight="fill" size={18} /> Fund the Sanctuary
          </Link>
          <Link to="/foster" className="inline-flex items-center gap-2 border border-primary/60 text-primary px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/10 transition-colors" data-testid="ranch-foster-cta">
            <PawPrint weight="fill" size={18} /> Foster a pit today
          </Link>
          <Link to="/shop" className="inline-flex items-center gap-2 border border-secondary/60 text-secondary px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-secondary/10 transition-colors neon-glow-aqua" data-testid="ranch-shop-cta">
            <PawPrint weight="fill" size={18} /> Shop the Movement
          </Link>
        </div>
      </section>
    </div>
  );
}
